import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import axios from 'axios';
import { HeaderNav } from '../components/HeaderNav';
import { Footer } from '../components/Footer';
import { AspectRatio } from '../components/ui/aspect-ratio';
import { Skeleton } from '../components/ui/skeleton';
import { motion } from 'framer-motion';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function CoversPage() {
  const [covers, setCovers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchCovers = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`${API_URL}/api/covers?pageSize=24`);
        setCovers(response.data);
      } catch (error) {
        console.error('Error fetching covers:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchCovers();
  }, []);

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  };

  return (
    <>
      <Helmet>
        <title>Magazine Covers Archive · B&B Magazine</title>
        <meta name="description" content="Browse the complete archive of B&B Magazine covers." />
        <link rel="canonical" href="https://cms-production-test.preview.emergentagent.com/covers" />
      </Helmet>

      <HeaderNav />

      <main>
        <section className="w-full py-12 md:py-16">
          <div className="container max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8">
            <div className="mb-10">
              <span className="uppercase tracking-[0.18em] text-xs text-muted-foreground font-ui">
                Archive
              </span>
              <h1 className="mt-2 font-display text-4xl md:text-5xl font-bold mb-4">
                Magazine Covers
              </h1>
              <p className="text-muted-foreground text-lg max-w-[65ch]">
                Explore every issue of B&B Magazine - bold covers for bold voices.
              </p>
            </div>

            {/* Covers Grid */}
            {loading ? (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
                {Array.from({ length: 12 }).map((_, i) => (
                  <div key={i} className="space-y-3">
                    <Skeleton className="w-full aspect-[3/4] rounded-lg" />
                    <Skeleton className="h-4 w-3/4" />
                  </div>
                ))}
              </div>
            ) : covers.length > 0 ? (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
                {covers.map((cover, index) => (
                  <motion.div
                    key={cover.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4, delay: index * 0.05 }}
                    className="group"
                  >
                    <div className="relative overflow-hidden rounded-lg border border-border shadow-[0_10px_30px_rgba(0,0,0,0.35)] transition-all duration-300 hover:shadow-[0_20px_60px_rgba(0,0,0,0.45)] hover:-translate-y-1">
                      <AspectRatio ratio={3/4}>
                        <img
                          src={cover.coverImageUrl}
                          alt={`Issue ${cover.issueNumber}`}
                          className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                          loading="lazy"
                        />
                      </AspectRatio>
                    </div>
                    <div className="mt-3">
                      <p className="text-xs uppercase tracking-[0.18em] text-primary font-ui">
                        Issue {cover.issueNumber}
                      </p>
                      {cover.title && (
                        <h3 className="font-display text-base font-semibold mt-1">
                          {cover.title}
                        </h3>
                      )}
                      {cover.publishedDate && (
                        <p className="text-xs text-muted-foreground mt-1">
                          {formatDate(cover.publishedDate)}
                        </p>
                      )}
                    </div>
                  </motion.div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <p className="text-muted-foreground">No covers found.</p>
              </div>
            )}
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}